import React from 'react'
import axios from 'axios'
import { MdNotificationsActive } from 'react-icons/md'
import { baseUrl } from '../../assets/Animations'
import { getRequestHeaders } from '../../api'
import './teacher-notification-test-button.css'

export const resolveTeacherNotificationId = (teacher) => String(
    teacher?.TeacherID
    || teacher?.teacherId
    || teacher?.teacherID
    || ''
).trim()

export const buildTeacherTestNotificationPayload = (teacher) => ({
    teacherId: resolveTeacherNotificationId(teacher),
    type: 'admin-test',
    title: 'Atharo test notification',
    body: 'This is a test notification from the Atharo admin dashboard.',
    route: '/notifications',
    data: {
        source: 'admin-teacher-modal',
        isTest: true,
    },
})

const formatSendFailure = (error) => (
    error?.response?.data?.error
    || error?.response?.data?.message
    || error?.message
    || 'The test notification could not be sent.'
)

const TeacherNotificationTestButton = ({ teacher }) => {
    const [sending, setSending] = React.useState(false)
    const [feedback, setFeedback] = React.useState(null)
    const teacherId = resolveTeacherNotificationId(teacher)

    const sendTestNotification = async () => {
        if (!teacherId || sending) return

        setSending(true)
        setFeedback(null)
        try {
            const headers = getRequestHeaders()
            const deviceResponse = await axios.get(
                `${baseUrl}/push-tokens/${encodeURIComponent(teacherId)}`,
                headers
            )
            const activeDevices = (Array.isArray(deviceResponse.data) ? deviceResponse.data : [])
                .filter((device) => !device?.disabledAt)

            if (activeDevices.length === 0) {
                setFeedback({
                    tone: 'warning',
                    message: 'No active native-app device is registered for this teacher.',
                })
                return
            }

            const response = await axios.post(
                `${baseUrl}/notifications/send`,
                buildTeacherTestNotificationPayload(teacher),
                headers
            )
            const notification = response?.data?.notification
            const status = String(notification?.status || '').toLowerCase()

            if (status === 'sent') {
                setFeedback({
                    tone: 'success',
                    message: `Accepted for delivery to ${activeDevices.length} registered device${activeDevices.length === 1 ? '' : 's'}.`,
                })
            } else if (status === 'no_devices') {
                setFeedback({
                    tone: 'warning',
                    message: 'The teacher no longer has an active registered device.',
                })
            } else if (status === 'queued' || status === 'sending') {
                setFeedback({
                    tone: 'success',
                    message: 'The test notification is queued for delivery.',
                })
            } else {
                setFeedback({
                    tone: 'error',
                    message: notification?.lastError || 'The notification service did not accept the test.',
                })
            }
        } catch (error) {
            console.error('Unable to send teacher test notification:', error)
            setFeedback({ tone: 'error', message: formatSendFailure(error) })
        } finally {
            setSending(false)
        }
    }

    return (
        <div className='teacher-notification-test'>
            <div className='teacher-notification-test__heading'>Native App</div>
            <button
                type='button'
                className='teacher-notification-test__button'
                disabled={sending || !teacherId}
                title={!teacherId ? 'This teacher does not have a Teacher ID' : ''}
                onClick={sendTestNotification}
            >
                <MdNotificationsActive size={16} aria-hidden='true' />
                {sending ? 'Checking device…' : 'Send test notification'}
            </button>
            {!teacherId && (
                <div className='teacher-notification-test__feedback teacher-notification-test__feedback--error'>
                    A Teacher ID is required.
                </div>
            )}
            {feedback && (
                <div
                    className={`teacher-notification-test__feedback teacher-notification-test__feedback--${feedback.tone}`}
                    role={feedback.tone === 'error' ? 'alert' : 'status'}
                >
                    {feedback.message}
                </div>
            )}
        </div>
    )
}

export default TeacherNotificationTestButton
